import { useCallback, useEffect, useMemo, useState } from 'react';
import { findMatches } from '../lib/chatSearch';
import { centerInList } from '../lib/keyboard';
import type { Message } from '../types';

/** Attribute on each message's row, so a found message can be scrolled to. */
export const MESSAGE_INDEX_ATTR = 'data-msg-index';

/**
 * Search inside a conversation: matches are highlighted where they are and you
 * jump between them (up = older, down = newer), starting at the most recent.
 */
export function useChatSearch(messages: Message[]) {
  const [open, setOpen] = useState(false);
  const [query, setQueryState] = useState('');
  const [pos, setPos] = useState(0);

  const matches = useMemo(() => (open ? findMatches(messages, query) : []), [open, messages, query]);
  const at = matches.length ? Math.min(Math.max(pos, 0), matches.length - 1) : -1;
  const current = at >= 0 ? matches[at] : null;

  const setQuery = useCallback((value: string) => {
    setQueryState(value);
    setPos(Number.MAX_SAFE_INTEGER); // the most recent match
  }, []);
  const toggle = useCallback(() => {
    setOpen((o) => !o);
    setQueryState('');
  }, []);
  const close = useCallback(() => {
    setOpen(false);
    setQueryState('');
  }, []);
  // Up: an older match. Down: a newer one. Both wrap around.
  const older = useCallback(() => setPos((p) => (matches.length ? (Math.min(p, matches.length - 1) - 1 + matches.length) % matches.length : 0)), [matches.length]);
  const newer = useCallback(() => setPos((p) => (matches.length ? (Math.min(p, matches.length - 1) + 1) % matches.length : 0)), [matches.length]);

  useEffect(() => {
    if (current == null) return;
    centerInList(document.querySelector<HTMLElement>(`[${MESSAGE_INDEX_ATTR}="${current}"]`));
  }, [current, query]);

  return { open, query, setQuery, toggle, close, matches, position: at + 1, total: matches.length, current, older, newer };
}
